import React, { useEffect, useState, useCallback } from "react";
import AppWrapper from "../AppWrapper";
import {
  Badge,
  BlockStack,
  Card,
  DataTable,
  LegacyCard,
  Page,
  Text,
  InlineStack,
  Spinner,
  ChoiceList,
  Filters,
  Button,
  Tooltip,
  Icon,
} from "@shopify/polaris";
import {
  AlertTriangleIcon,
  ViewIcon,
  WrenchIcon,
} from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { numberWithCommas, formatPlate } from "../../utility/funcs";
import { useNavigate } from "react-router-dom";

function ServicesDashboard() {
  const [nextServices, setNextServices] = useState(undefined);
  const [serviceChoices, setServiceChoices] = useState([]);
  const [servicesToFilter, setServicesToFilter] = useState([]);

  // 👇 NUEVO
  const [customerChoices, setCustomerChoices] = useState([]);
  const [customersToFilter, setCustomersToFilter] = useState([]);

  const [stateChoices, setStateChoices] = useState([]);
  const [statesToFilter, setStatesToFilter] = useState([]);

  const navigate = useNavigate();

  // ------------------------------
  // Fetch & prepare data
  // ------------------------------
  useEffect(() => {
    const fetchData = async () => {
      const { data, error } = await supabase.rpc("get_upcoming_services");
      if (error) {
        console.error(error);
        return;
      }

      // Format services_due
      const formatted = data.map((service) => {
        const services_due =
          service.services_due
            ?.replaceAll("Cambio de ", "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean) || [];

        return {
          ...service,
          services_due,
          customer: service.customer ?? "", // 👈 AQUÍ SE CAMBIA null → ""
        };
      });

      // Unique service types
      const uniqueServices = [
        ...new Set(
          data.flatMap(
            (s) =>
              s.services_due
                ?.replaceAll("Cambio de ", "")
                .split(",")
                .map((x) => x.trim())
                .filter(Boolean) || [],
          ),
        ),
      ].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));

      setServiceChoices(
        uniqueServices.map((service) => ({
          label: service,
          value: service,
        })),
      );

      // 👇 NUEVO: unique customers
      const uniqueCustomers = [
        ...new Set(data.map((s) => s.customer).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));

      setCustomerChoices([
        { label: "Corto Plazo", value: "" },
        ...uniqueCustomers.map((c) => ({
          label: c,
          value: c,
        })),
      ]);

      const uniqueStates = [
        ...new Set(data.map((s) => s.current_state).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));

      setStateChoices(
        uniqueStates.map((state) => ({
          label: state,
          value: state,
        })),
      );

      setNextServices(formatted);
    };

    fetchData();
  }, []);

  // ------------------------------
  // Helpers
  // ------------------------------
  const getBadgeTone = (kms, services_due) => {
    const hasCambioDeAceite = services_due.includes("Aceite");

    if (kms < 0) {
      return hasCambioDeAceite ? "critical" : "warning";
    }

    if (kms < 250) return "attention";
    if (kms < 500) return "warning";
    return "success";
  };


  const servicesBadges = (next_services) => {
    if (servicesToFilter.length === 0)
      return (
        <InlineStack gap="100">
          {next_services.map((service) => (
            <Badge key={service} tone="info">
              {service}
            </Badge>
          ))}
        </InlineStack>
      );

    return (
      <InlineStack gap="100">
        {next_services.map((service) => (
          <Badge
            key={service}
            tone={servicesToFilter.includes(service) ? "info" : ""}
          >
            {service}
          </Badge>
        ))}
      </InlineStack>
    );
  };

  // ------------------------------
  // Filtering
  // ------------------------------
  const handleServicesFilterSelect = useCallback(
    (value) => setServicesToFilter(value),
    [],
  );

  // 👇 NUEVO
  const handleCustomerFilterSelect = useCallback(
    (value) => setCustomersToFilter(value),
    [],
  );

  const handleStateFilterSelect = useCallback(
    (value) => setStatesToFilter(value),
    [],
  );

  const handleFiltersClearAll = useCallback(() => {
    setServicesToFilter([]);
    setCustomersToFilter([]);
    setStatesToFilter([]);
  }, []);

  const appliedFilters = [];

  if (servicesToFilter.length > 0) {
    appliedFilters.push({
      key: "service",
      label: `Tipo de Servicio: ${servicesToFilter.join(", ")}`,
      onRemove: () => setServicesToFilter([]),
    });
  }

  if (customersToFilter.length > 0) {
    appliedFilters.push({
      key: "customer",
      label: `Cliente: ${customersToFilter.join(", ")}`,
      onRemove: () => setCustomersToFilter([]),
    });
  }

  if (statesToFilter.length > 0) {
    appliedFilters.push({
      key: "state",
      label: `Estado: ${statesToFilter.join(", ")}`,
      onRemove: () => setStatesToFilter([]),
    });
  }

  const filters = [
    {
      key: "service",
      label: "Tipo de Servicio",
      filter: (
        <ChoiceList
          title="Servicio"
          titleHidden
          choices={serviceChoices}
          selected={servicesToFilter}
          onChange={handleServicesFilterSelect}
          allowMultiple
        />
      ),
      shortcut: true,
      pinned: true,
    },
    // 👇 NUEVO
    {
      key: "customer",
      label: "Cliente",
      filter: (
        <ChoiceList
          title="Cliente"
          titleHidden
          choices={customerChoices}
          selected={customersToFilter}
          onChange={handleCustomerFilterSelect}
          allowMultiple
        />
      ),
      shortcut: true,
      pinned: true,
    },
    {
      key: "state",
      label: "Estado",
      filter: (
        <ChoiceList
          title="Estado"
          titleHidden
          choices={stateChoices}
          selected={statesToFilter}
          onChange={handleStateFilterSelect}
          allowMultiple
        />
      ),
      shortcut: true,
      pinned: true,
    },
  ];

  // ------------------------------
  // Apply filtering
  // ------------------------------

  let filteredServices = nextServices;

  if (servicesToFilter.length > 0) {
    filteredServices = filteredServices.filter((service) =>
      service.services_due.some((s) => servicesToFilter.includes(s)),
    );
  }

  if (customersToFilter.length > 0) {
    filteredServices = filteredServices.filter((service) =>
      customersToFilter.includes(service.customer),
    );
  }

  if (statesToFilter.length > 0) {
    filteredServices = filteredServices.filter((service) =>
      statesToFilter.includes(service.current_state),
    );
  }

  const odometerVerificationTooltip = (days) => {
    if (days != null && days <= 21) return;
    return (
      <div>
        <Icon source={AlertTriangleIcon} tone="caution" />
      </div>
    );
  };

  // ------------------------------
  // Table rows
  // ------------------------------
  const rows =
    filteredServices?.map((service) => [
      `${formatPlate(service.plate)} (${service.model_name})`,
      <InlineStack gap={50}>
        {odometerVerificationTooltip(service.days_since_verification)}
        {numberWithCommas(service.current_odometer)}
      </InlineStack>,
      servicesBadges(service.services_due),
      <Badge tone={getBadgeTone(service.kms_remaining, service.services_due)}>
        {numberWithCommas(service.kms_remaining)}
      </Badge>,
      service.current_state,
      service.reservation_id ? (
        <a
          href={`/reservation?id=${service.reservation_id}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/reservation?id=${service.reservation_id}`);
          }}
          style={{
            width: 160,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            color: "var(--p-color-text-link)",
            textDecoration: "none",
          }}
          title={service.customer || ""}
        >
          {service.customer || ""}
        </a>
      ) : (
        <div
          style={{
            width: 160,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={service.customer || ""}
        >
          {service.customer || ""}
        </div>
      ),
      <InlineStack gap={100}>
        <Button
          icon={ViewIcon}
          variant="plain"
          onClick={() => navigate(`/cars/${service.car_id}`)}
        />
        <Button
          icon={WrenchIcon}
          variant="plain"
          onClick={() => navigate(`/services/new?plate=${service.plate}`)}
        />
      </InlineStack>,
    ]) ?? [];

  // ------------------------------
  // Page render
  // ------------------------------
  const pageContent = () => {
    if (!nextServices) return <Spinner size="small" />;
    return (
      <div style={{ marginBottom: "2rem" }}>
        <BlockStack gap="300">
          <Text variant="headingMd">Próximos Servicios</Text>
          <LegacyCard>
            <Filters
              filters={filters}
              appliedFilters={appliedFilters}
              onClearAll={handleFiltersClearAll}
              hideQueryField
            />
            <DataTable
              columnContentTypes={["text", "text", "text", "text", "text"]}
              headings={[
                "Vehículo",
                "Odómetro",
                "Servicios",
                "Kms Restantes",
                "Estado",
                "Cliente",
                "",
              ]}
              rows={rows}
            />
          </LegacyCard>
        </BlockStack>
      </div>
    );
  };

  return (
    <AppWrapper>
      <Page title="Servicios">{pageContent()}</Page>
    </AppWrapper>
  );
}

export default ServicesDashboard;
